import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { fmtEuro } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Loader2, UploadCloud, FileText, Banknote, Table } from "lucide-react";
import { toast } from "sonner";

const fmtLoanDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR", { month: "short", year: "numeric" }); } catch { return "—"; }
};
const fmtFullDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }); } catch { return ""; }
};

const emptyLoan = {
  name: "Emprunt", loan_type: "achat", loan_bank: "", loan_amount: "", loan_rate: "", loan_duration_months: "",
  loan_start_date: "", loan_fees: "", insurance_monthly: "", category_id: "none",
};

export default function PropertyLoans({ propertyId, categories = [], onChange }) {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyLoan);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [contractBusy, setContractBusy] = useState(false);
  const [stagedSchedule, setStagedSchedule] = useState(null);
  const contractRef = useRef();

  const [schedOpen, setSchedOpen] = useState(false);
  const [schedLoan, setSchedLoan] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [attaching, setAttaching] = useState(false);
  const fileRef = useRef();

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoan, setDetailLoan] = useState(null);
  const [detailRows, setDetailRows] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const openDetail = async (l) => {
    if (!l.schedule_count) return;
    setDetailLoan(l); setDetailRows([]); setDetailOpen(true); setDetailLoading(true);
    try { const { data } = await api.get(`/loans/${l.id}/schedule`); setDetailRows(data.schedule || []); }
    catch { toast.error("Impossible de charger le tableau d'amortissement"); }
    finally { setDetailLoading(false); }
  };

  const load = () => {
    setLoading(true);
    api.get(`/properties/${propertyId}/loans`).then((r) => setLoans(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, [propertyId]);

  const num = (v) => Number(v) || 0;
  const openNew = () => { setForm(emptyLoan); setEditId(null); setStagedSchedule(null); setOpen(true); };
  const openEdit = (l) => {
    setForm({
      name: l.name || "Emprunt", loan_type: l.loan_type || "achat", loan_bank: l.loan_bank || "", loan_amount: l.loan_amount ? String(l.loan_amount) : "",
      loan_rate: l.loan_rate ? String(l.loan_rate) : "", loan_duration_months: l.loan_duration_months ? String(l.loan_duration_months) : "",
      loan_start_date: (l.loan_start_date || "").slice(0, 10), loan_fees: l.loan_fees ? String(l.loan_fees) : "",
      insurance_monthly: l.insurance_monthly ? String(l.insurance_monthly) : "", category_id: l.category_id || "none",
    });
    setEditId(l.id); setStagedSchedule(null); setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nom de l'emprunt requis"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name, loan_type: form.loan_type, loan_bank: form.loan_bank, loan_amount: num(form.loan_amount), loan_rate: num(form.loan_rate),
        loan_duration_months: Math.round(num(form.loan_duration_months)), loan_start_date: form.loan_start_date,
        loan_fees: num(form.loan_fees), insurance_monthly: num(form.insurance_monthly),
        category_id: form.category_id === "none" ? null : form.category_id,
      };
      if (editId) await api.put(`/loans/${editId}`, payload);
      else { await api.post(`/properties/${propertyId}/loans`, payload); }
      let lid = editId;
      if (!editId) { const { data } = await api.get(`/properties/${propertyId}/loans`); lid = (data[data.length - 1] || {}).id; }
      if (stagedSchedule && stagedSchedule.length && lid) {
        await api.put(`/loans/${lid}/schedule`, { schedule: stagedSchedule });
      }
      setStagedSchedule(null);
      toast.success(editId ? "Emprunt modifié" : "Emprunt ajouté");
      setOpen(false); load(); onChange && onChange();
    } catch { toast.error("Erreur lors de l'enregistrement"); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet emprunt et son tableau d'amortissement ?")) return;
    await api.delete(`/loans/${id}`); toast.success("Emprunt supprimé"); load(); onChange && onChange();
  };

  const parseScheduleFile = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post("/properties/import-schedule", fd, { headers: { "Content-Type": "multipart/form-data" } });
    const jobId = data.job_id; const started = Date.now();
    while (Date.now() - started < 300000) {
      await new Promise((r) => setTimeout(r, 2500));
      const { data: job } = await api.get(`/properties/schedule-status/${jobId}`);
      if (job.status === "done") return job.schedule || [];
      if (job.status === "error") throw new Error(job.error || "Erreur d'analyse");
    }
    throw new Error("L'analyse prend trop de temps.");
  };

  const handleContract = async (file) => {
    if (!file) return;
    setContractBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/properties/import-loan", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const f = data.fields || {};
      setForm((prev) => ({
        ...prev,
        loan_bank: f.loan_bank ?? prev.loan_bank,
        name: prev.name === "Emprunt" && f.loan_bank ? f.loan_bank : prev.name,
        loan_amount: f.loan_amount != null ? String(f.loan_amount) : prev.loan_amount,
        loan_rate: f.loan_rate != null ? String(f.loan_rate) : prev.loan_rate,
        loan_duration_months: f.loan_duration_months != null ? String(f.loan_duration_months) : prev.loan_duration_months,
        loan_start_date: f.loan_start_date ?? prev.loan_start_date,
        loan_fees: f.loan_fees != null ? String(f.loan_fees) : prev.loan_fees,
        insurance_monthly: f.insurance_monthly != null ? String(f.insurance_monthly) : prev.insurance_monthly,
      }));
      toast.success("Contrat analysé — champs pré-remplis.");
    } catch (e) { toast.error(e.response?.data?.detail || "Impossible de lire le contrat"); setContractBusy(false); return; }
    // Le même fichier contient souvent le tableau d'amortissement → on le lit et on l'attache aussi.
    try {
      const rows = await parseScheduleFile(file);
      if (rows && rows.length) {
        if (editId) {
          await api.put(`/loans/${editId}/schedule`, { schedule: rows });
          toast.success(`${rows.length} échéances importées et rattachées`);
          load(); onChange && onChange();
        } else {
          setStagedSchedule(rows);
          toast.success(`${rows.length} échéances détectées — enregistrées à la création de l'emprunt`);
        }
      }
    } catch (e) { toast.warning("Aucun tableau d'amortissement lisible dans ce fichier. Utilisez le bouton d'import du tableau (icône ↑) si besoin."); }
    finally { setContractBusy(false); }
  };

  const openSched = (l) => { setSchedLoan(l); setSchedule(null); setSchedOpen(true); };
  const handleFile = async (file) => {
    if (!file || !schedLoan) return;
    setAnalyzing(true); setSchedule(null);
    try {
      const rows = await parseScheduleFile(file);
      if (!rows.length) { toast.warning("Aucune échéance détectée dans ce fichier."); return; }
      await api.put(`/loans/${schedLoan.id}/schedule`, { schedule: rows });
      setSchedule(rows);
      toast.success(`${rows.length} échéances importées et enregistrées`);
      load(); onChange && onChange();
    } catch (e) { toast.error(e.message || e.response?.data?.detail || "Erreur lors de l'analyse"); }
    finally { setAnalyzing(false); }
  };
  const attach = async () => {
    if (!schedule || schedule.length === 0) return;
    setAttaching(true);
    try {
      await api.put(`/loans/${schedLoan.id}/schedule`, { schedule });
      toast.success("Tableau d'amortissement enregistré");
      setSchedOpen(false); load(); onChange && onChange();
    } catch { toast.error("Erreur lors de l'enregistrement"); } finally { setAttaching(false); }
  };

  const f = (key, label, type = "text", ph = "") => (
    <div className="space-y-1.5"><Label className="text-xs">{label}</Label>
      <Input type={type} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} placeholder={ph} className="h-8" data-testid={`loan-${key}-input`} />
    </div>
  );

  const scheduleSummary = () => {
    if (!schedule || schedule.length === 0) return null;
    const s = [...schedule].sort((a, b) => (a.year - b.year) || (a.month - b.month));
    return { count: s.length, initial: (s[0].remaining_balance || 0) + (s[0].principal || 0), first: s[0], last: s[s.length - 1] };
  };

  return (
    <div className="mt-3 rounded-lg bg-secondary/30 p-3" data-testid={`loans-panel-${propertyId}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5"><Banknote className="w-3.5 h-3.5" />Emprunts hypothécaires</div>
        <Button size="sm" variant="outline" onClick={openNew} data-testid={`add-loan-${propertyId}`}><Plus className="w-3.5 h-3.5 mr-1" />Ajouter un emprunt</Button>
      </div>
      {loading ? (
        <div className="h-12 flex items-center justify-center"><Loader2 className="w-4 h-4 animate-spin text-primary" /></div>
      ) : loans.length === 0 ? (
        <p className="text-xs text-muted-foreground py-2">Aucun emprunt. Ajoutez un contrat (import ou saisie manuelle).</p>
      ) : (
        <div className="space-y-2">
          {loans.map((l) => (
            <div key={l.id} className="rounded-md border border-border bg-card p-3" data-testid={`loan-row-${l.id}`}>
              <div className="flex items-center gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium flex items-center gap-2 flex-wrap">{l.name}
                    <Badge variant="secondary" className="text-[10px]">{l.loan_type === "travaux" ? "Travaux" : "Achat du bien"}</Badge>
                    {l.loan_bank && <span className="text-xs text-muted-foreground font-normal">· {l.loan_bank}</span>}
                    {l.schedule_count > 0 ? <Badge variant="outline" className="text-[10px]">{l.schedule_count} éch.</Badge> : <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-600">sans tableau</Badge>}
                  </div>
                </div>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openSched(l)} title="Importer l'amortissement" data-testid={`loan-import-schedule-${l.id}`}><UploadCloud className="w-3.5 h-3.5" /></Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(l)} data-testid={`edit-loan-${l.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => remove(l.id)} data-testid={`delete-loan-${l.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
              <button
                type="button"
                onClick={() => openDetail(l)}
                disabled={!l.schedule_count}
                className={`w-full text-left rounded-md mt-2 -mx-1 px-1 py-1 transition-colors ${l.schedule_count ? "hover:bg-secondary/60 cursor-pointer" : "cursor-default"}`}
                data-testid={`loan-detail-open-${l.id}`}
                title={l.schedule_count ? "Voir le tableau d'amortissement" : "Aucun tableau d'amortissement"}
              >
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div><span className="text-muted-foreground">Emprunté</span><div className="font-num">{fmtEuro(l.loan_amount)}</div></div>
                  <div><span className="text-muted-foreground">Remboursé</span><div className="font-num text-emerald-600">{fmtEuro(l.paid_principal)}</div></div>
                  <div><span className="text-muted-foreground">Restant dû</span><div className="font-num text-destructive">{fmtEuro(l.remaining_balance)}</div></div>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-2 text-xs">
                  <div><span className="text-muted-foreground">Taux</span><div className="font-num">{l.loan_rate ? `${l.loan_rate} %` : "—"}</div></div>
                  <div><span className="text-muted-foreground">Début</span><div className="font-num">{fmtLoanDate(l.schedule_start || l.loan_start_date)}</div></div>
                  <div><span className="text-muted-foreground">Fin</span><div className="font-num">{fmtLoanDate(l.schedule_end)}</div></div>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                  <div><span className="text-muted-foreground">Total capital</span><div className="font-num">{fmtEuro(l.loan_amount)}</div></div>
                  <div><span className="text-muted-foreground">Total intérêts</span><div className="font-num text-amber-600">{fmtEuro(l.total_interest_scheduled)}</div></div>
                </div>
                {l.schedule_count > 0 && (
                  <div className="flex items-center gap-1 mt-2 text-[11px] text-primary font-medium">
                    <Table className="w-3 h-3" />Voir le tableau d'amortissement ({l.schedule_count} éch.)
                  </div>
                )}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Loan add/edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="loan-dialog">
          <DialogHeader><DialogTitle className="font-display">{editId ? "Modifier l'emprunt" : "Nouvel emprunt"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="flex items-center justify-between rounded-lg border border-dashed border-border p-2.5">
              <span className="text-xs text-muted-foreground">Lire le contrat / tableau d'amortissement (PDF) pour tout pré-remplir</span>
              <input ref={contractRef} type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" onChange={(e) => handleContract(e.target.files?.[0])} data-testid="loan-contract-input" />
              <Button type="button" variant="outline" size="sm" disabled={contractBusy} onClick={() => contractRef.current?.click()} data-testid="loan-contract-button">
                {contractBusy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileText className="w-3.5 h-3.5 mr-1.5" />}Lire un contrat
              </Button>
            </div>
            {f("name", "Nom de l'emprunt", "text", "Ex: Crédit principal")}
            <div className="space-y-1.5"><Label className="text-xs">Type de crédit</Label>
              <Select value={form.loan_type} onValueChange={(v) => setForm({ ...form, loan_type: v })}>
                <SelectTrigger className="h-8" data-testid="loan-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="achat">Achat du bien</SelectItem>
                  <SelectItem value="travaux">Travaux</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">{f("loan_bank", "Banque", "text", "Ex: Belfius")}{f("loan_amount", "Montant emprunté (€)", "number", "0,00")}</div>
            <div className="grid grid-cols-2 gap-3">{f("loan_rate", "Taux annuel (%)", "number", "0,00")}{f("loan_duration_months", "Durée (mois)", "number", "240")}</div>
            <div className="grid grid-cols-2 gap-3">{f("loan_start_date", "Date de début", "date")}{f("loan_fees", "Frais de dossier (€)", "number", "0,00")}</div>
            {f("insurance_monthly", "Assurance solde restant dû / mois (€)", "number", "0,00")}
            <div className="space-y-1.5"><Label className="text-xs">Catégorie de budget liée (optionnel)</Label>
              <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                <SelectTrigger className="h-8" data-testid="loan-category-select"><SelectValue placeholder="Aucune" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucune</SelectItem>
                  {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button><Button onClick={save} disabled={saving} data-testid="save-loan-button">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule import dialog */}
      <Dialog open={schedOpen} onOpenChange={setSchedOpen}>
        <DialogContent data-testid="loan-schedule-dialog">
          <DialogHeader><DialogTitle className="font-display">Amortissement — {schedLoan?.name}</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">Importez le tableau d'amortissement de CET emprunt (Excel/CSV/PDF). Il est enregistré automatiquement dès qu'il est lu.</p>
          <div className="py-2">
            <div className={`border-2 border-dashed rounded-xl p-6 flex flex-col items-center text-center ${analyzing ? "border-primary bg-primary/5" : "border-border"}`}>
              <div className="w-11 h-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-2">{analyzing ? <Loader2 className="w-5 h-5 animate-spin" /> : <UploadCloud className="w-5 h-5" />}</div>
              <p className="text-sm font-medium">{analyzing ? "Analyse en cours…" : "Choisir le fichier"}</p>
              <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv,.pdf" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="loan-schedule-file-input" />
              <Button className="mt-3" size="sm" disabled={analyzing} onClick={() => fileRef.current?.click()} data-testid="loan-schedule-choose">Choisir un fichier</Button>
            </div>
            {schedule && schedule.length > 0 && (() => {
              const s = scheduleSummary();
              return (
                <div className="mt-3 rounded-lg bg-secondary/50 p-3 text-sm space-y-1" data-testid="loan-schedule-preview">
                  <div className="flex justify-between"><span className="text-muted-foreground">Échéances</span><span className="font-num">{s.count}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Capital initial</span><span className="font-num">{fmtEuro(s.initial)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Restant dû (fin)</span><span className="font-num">{fmtEuro(s.last.remaining_balance)}</span></div>
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSchedOpen(false)} data-testid="loan-schedule-close">{schedule && schedule.length > 0 ? "Terminé" : "Fermer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Amortization detail dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="loan-amort-detail-dialog">
          <DialogHeader><DialogTitle className="font-display">Tableau d'amortissement — {detailLoan?.name}</DialogTitle></DialogHeader>
          {detailLoan && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs -mt-1">
              <div><span className="text-muted-foreground">Taux</span><div className="font-num">{detailLoan.loan_rate ? `${detailLoan.loan_rate} %` : "—"}</div></div>
              <div><span className="text-muted-foreground">Début</span><div className="font-num">{fmtLoanDate(detailLoan.schedule_start || detailLoan.loan_start_date)}</div></div>
              <div><span className="text-muted-foreground">Fin</span><div className="font-num">{fmtLoanDate(detailLoan.schedule_end)}</div></div>
              <div><span className="text-muted-foreground">Restant dû</span><div className="font-num text-destructive">{fmtEuro(detailLoan.remaining_balance)}</div></div>
            </div>
          )}
          <div className="flex-1 overflow-y-auto mt-2 rounded-lg border border-border" data-testid="loan-amort-detail-body">
            {detailLoading ? (
              <div className="h-40 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
            ) : detailRows.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-10">Aucune échéance.</p>
            ) : (
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-secondary/80 backdrop-blur">
                  <tr className="text-muted-foreground">
                    <th className="text-left font-medium px-3 py-2">Date</th>
                    <th className="text-right font-medium px-3 py-2">Mensualité</th>
                    <th className="text-right font-medium px-3 py-2">Intérêts</th>
                    <th className="text-right font-medium px-3 py-2">Capital</th>
                    <th className="text-right font-medium px-3 py-2">Restant dû</th>
                  </tr>
                </thead>
                <tbody>
                  {detailRows.map((r, i) => (
                    <tr key={i} className="border-t border-border/60" data-testid={`amort-row-${i}`}>
                      <td className="px-3 py-1.5">{fmtFullDate(r.date)}</td>
                      <td className="px-3 py-1.5 text-right font-num">{fmtEuro(r.payment)}</td>
                      <td className="px-3 py-1.5 text-right font-num text-muted-foreground">{fmtEuro(r.interest)}</td>
                      <td className="px-3 py-1.5 text-right font-num text-emerald-600">{fmtEuro(r.principal)}</td>
                      <td className="px-3 py-1.5 text-right font-num">{fmtEuro(r.remaining_balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailOpen(false)} data-testid="loan-amort-detail-close">Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
